/**
 * Two-way synchroniser between a view's MobX state and the URL query string.
 *
 * Flow (conceptually one-directional, pragmatically two-phase):
 *   - HYDRATE  (URL -> stores): on initial mount and on browser back/forward
 *     (POP) navigations. Runs once reference data is ready. Invalid values are
 *     ignored by the decoders so the view falls back to defaults gracefully.
 *     Immediately after decoding it performs a single store -> URL write so
 *     that state the stores already hold when the view mounts (e.g. a country
 *     picked via a map click, or selections kept after navigating away and
 *     back) is reflected in the URL - something REFLECT alone would miss.
 *   - REFLECT  (stores -> URL): a debounced MobX reaction serialises the
 *     relevant slice and writes it back with `replace` (no history spam, no
 *     page reload).
 *
 * Loops are avoided because:
 *   - REFLECT writes use `replace`, and HYDRATE ignores REPLACE navigations.
 *   - A guard flag suppresses REFLECT while HYDRATE is applying URL values.
 */
import { useEffect, useRef } from 'react';
import {
    useLocation,
    useNavigationType,
    useSearchParams,
} from 'react-router-dom';
import { comparer, reaction, when, IReactionDisposer } from 'mobx';
import { useCurrentView } from '../../hooks/useCurrentView';
import { GRAPH_VIEWS, VIEW } from '../../interfaces';
import { encodeVirtualToday, decodeVirtualToday } from './codecs';
import { ViewUrlSchema } from './viewSchemas';

const REFLECT_DEBOUNCE_MS = 300;

const VIRTUAL_TODAY_PARAM = 'virtual_today';
const VIRTUAL_TODAY_VIEWS = new Set<VIEW>([
    ...GRAPH_VIEWS,
    VIEW.SORT_BY_RISK,
    VIEW.SORT_BY_CONTINGENT_RISK,
]);

export function useViewUrlSync(schema: ViewUrlSchema): void {
    const view = useCurrentView();
    const location = useLocation();
    const navType = useNavigationType();
    const [, setSearchParams] = useSearchParams();

    const hydratingRef = useRef(false);
    const lastHydratedKeyRef = useRef<string>(null);
    const searchRef = useRef(location.search);
    searchRef.current = location.search;

    // Serialise the active view's current store slice (null until ready).
    const encodeCurrent = (): Record<string, string> | null => {
        if (!schema.isReady()) {
            return null;
        }
        const encoded = schema.encode(view);
        if (VIRTUAL_TODAY_VIEWS.has(view)) {
            encoded[VIRTUAL_TODAY_PARAM] = encodeVirtualToday();
        }
        return encoded;
    };

    // Merge an encoded slice into the URL via replace (no-op if unchanged).
    const writeToUrl = (next: Record<string, string> | null): void => {
        if (!next) {
            return;
        }
        const current = new URLSearchParams(searchRef.current);
        const merged = new URLSearchParams(searchRef.current);
        const ownedKeys = VIRTUAL_TODAY_VIEWS.has(view)
            ? [...schema.ownedKeys, VIRTUAL_TODAY_PARAM]
            : schema.ownedKeys;
        ownedKeys.forEach(key => {
            if (next[key] != null) {
                merged.set(key, next[key]);
            } else {
                merged.delete(key);
            }
        });
        if (merged.toString() === current.toString()) {
            return;
        }
        setSearchParams(merged, { replace: true });
    };

    // HYDRATE: URL -> stores (initial mount + back/forward only).
    useEffect(() => {
        if (navType === 'REPLACE') {
            return;
        }
        if (lastHydratedKeyRef.current === location.key) {
            return;
        }
        lastHydratedKeyRef.current = location.key;

        const params = Object.fromEntries(new URLSearchParams(location.search));
        const dispose = when(
            () => schema.isReady(),
            () => {
                hydratingRef.current = true;
                try {
                    schema.decode(view, params);
                    if (VIRTUAL_TODAY_VIEWS.has(view)) {
                        decodeVirtualToday(params[VIRTUAL_TODAY_PARAM]);
                    }
                    // Push the resulting store state straight back to the URL.
                    // REFLECT below is fireImmediately:false, so state set
                    // *before* this view mounted (a country chosen by a map
                    // click, or selections the stores still hold after
                    // navigating away and back) would otherwise never reach the
                    // URL. No-op when the URL already matches.
                    writeToUrl(encodeCurrent());
                } finally {
                    // Release after the current tick so REFLECT does not echo
                    // the just-applied values straight back to the URL.
                    setTimeout(() => {
                        hydratingRef.current = false;
                    }, 0);
                }
            }
        );
        return dispose;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key, navType, view]);

    // REFLECT: stores -> URL (debounced).
    useEffect(() => {
        const dispose: IReactionDisposer = reaction(
            encodeCurrent,
            next => {
                if (hydratingRef.current) {
                    return;
                }
                writeToUrl(next);
            },
            {
                fireImmediately: false,
                delay: REFLECT_DEBOUNCE_MS,
                equals: comparer.structural,
            }
        );
        return dispose;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);
}
